/**
 * RestoreCompletedRidesDialog · v2.8.9
 * ═════════════════════════════════════════════════════════════════════
 * One-click, SAFE repair for the Completed Rides ledger. Two-step:
 * Preview (dry-run, read-only) → Apply. It ONLY updates status/links/
 * attribution and RECOVERS missing totals — it never deletes and never
 * overwrites a real price with zero.
 *
 * Phases (see POST /api/admin/completed-rides/repair):
 *   1) link    — tie old unlinked meter trips to their dispatch booking
 *   2) restore — bring driver-completed dispatch trips back into Completed Rides
 *   3) totals  — recover $0 completed-ride totals from the dispatcher's price
 */
import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Wrench, CheckCircle2, Link2, Undo2, DollarSign } from "lucide-react";
import { toast } from "sonner";

export const RestoreCompletedRidesDialog = ({ open, onClose, onApplied }) => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [applied, setApplied] = useState(false);

  const run = async (dry_run) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/completed-rides/repair", { dry_run });
      setReport(data);
      if (!dry_run) {
        setApplied(true);
        toast.success(
          `Restored ${data.restore.restored} ride${data.restore.restored === 1 ? "" : "s"} · ` +
          `linked ${data.link.linked} · recovered ${data.totals.found} total${data.totals.found === 1 ? "" : "s"}`,
        );
        onApplied?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Repair failed");
    } finally { setBusy(false); }
  };

  const close = () => { setReport(null); setApplied(false); onClose?.(); };
  const nothingToDo = report && !report.link.linked && !report.restore.restored && !report.totals.found;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restore-rides-dialog">
        <DialogHeader>
          <DialogTitle className="text-emerald-300 flex items-center gap-2">
            <Wrench className="h-5 w-5"/>Repair / Restore Completed Rides
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800 p-4 text-xs text-zinc-400 space-y-2">
            <p>
              Finds dispatch trips a driver completed on the meter that never made it back into
              this ledger, <span className="text-emerald-300 font-bold">restores</span> them,
              de-duplicates any meter copies against their booking, and recovers totals that were
              left at <span className="text-amber-300 font-bold">$0.00</span> using the
              <span className="text-emerald-300 font-bold"> dispatcher&apos;s price</span>.
            </p>
            <p className="text-zinc-500">
              <span className="text-emerald-300">Preview is read-only.</span> Nothing is written until
              you press Apply. It never deletes data and never overwrites a real price with zero.
              Ambiguous matches (2+ possible bookings) are skipped, never guessed.
            </p>
          </Card>

          {report && (
            <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3" data-testid="restore-rides-report">
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat icon={<Link2 className="h-3.5 w-3.5"/>} label="Trips linked" value={report.link.linked} testid="restore-stat-linked"/>
                <Stat icon={<Undo2 className="h-3.5 w-3.5"/>} label="Rides restored" value={report.restore.restored} accent="emerald" testid="restore-stat-restored"/>
                <Stat icon={<DollarSign className="h-3.5 w-3.5"/>} label="Totals recovered" value={report.totals.found} accent="amber" testid="restore-stat-totals"/>
              </div>

              <div className="text-[11px] text-zinc-500 grid grid-cols-2 gap-x-4 gap-y-0.5">
                <span>Skipped — ambiguous: <span className="text-zinc-300">{report.link.skipped_ambiguous}</span></span>
                <span>Skipped — no phone: <span className="text-zinc-300">{report.link.skipped_no_phone}</span></span>
                <span>Skipped — no match: <span className="text-zinc-300">{report.link.skipped_no_match}</span></span>
                <span>Open rides found: <span className="text-zinc-300">{report.restore.found}</span></span>
              </div>

              {report.restore.examples?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Rides being restored</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto font-mono text-[11px]">
                    {report.restore.examples.map((r, i) => (
                      <div key={`${r.ride_id}-${i}`} className="flex items-center justify-between gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
                        <span className="text-zinc-400 truncate">{r.ride_id}</span>
                        <span className="text-emerald-300">{r.was_status} → completed</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.totals.examples?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Totals being recovered (dispatcher price)</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto font-mono text-[11px]">
                    {report.totals.examples.map((r, i) => (
                      <div key={`${r.ride_id}-${i}`} className="flex items-center justify-between gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
                        <span className="text-zinc-400 truncate">{r.ride_id}</span>
                        <span className="text-amber-300">$0.00 → ${Number(r.recovered_total).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {nothingToDo && (
                <div className="text-emerald-300 text-xs flex items-center gap-1.5" data-testid="restore-nothing-to-do">
                  <CheckCircle2 className="h-4 w-4"/>Everything is already in sync — nothing to restore.
                </div>
              )}

              {applied && (
                <div className="text-emerald-300 text-xs flex items-center gap-1.5" data-testid="restore-applied">
                  <CheckCircle2 className="h-4 w-4"/>Applied. The ledger has been refreshed.
                </div>
              )}
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} data-testid="restore-rides-close">Close</Button>
          <Button
            variant="outline" onClick={() => run(true)} disabled={busy}
            data-testid="restore-rides-preview"
            className="border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20"
          >
            {busy && !applied ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Scanning…</> : "Preview"}
          </Button>
          <Button
            onClick={() => {
              if (!window.confirm(
                `Restore ${report.restore.restored} ride${report.restore.restored === 1 ? "" : "s"}, ` +
                `link ${report.link.linked} trip${report.link.linked === 1 ? "" : "s"}, and recover ` +
                `${report.totals.found} total${report.totals.found === 1 ? "" : "s"}?\n\n` +
                `Nothing is deleted. Prices are only ever filled in, never lowered to zero.`,
              )) return;
              run(false);
            }}
            disabled={busy || !report || nothingToDo || applied}
            data-testid="restore-rides-apply"
            className="bg-emerald-400 hover:bg-emerald-300 text-zinc-900 font-bold"
          >
            {busy && report ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Restoring…</> : "Apply Repair"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ icon, label, value, accent, testid }) => (
  <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold flex items-center justify-center gap-1">{icon}{label}</div>
    <div
      className={`text-lg font-black tabular-nums ${
        accent === "emerald" ? "text-emerald-300" : accent === "amber" ? "text-amber-300" : "text-zinc-200"
      }`}
      data-testid={testid}
    >
      {value ?? 0}
    </div>
  </div>
);

export default RestoreCompletedRidesDialog;
